import React, { useEffect, useMemo, useRef, useState } from 'react';
import { GoogleMap, MarkerF, useLoadScript } from '@react-google-maps/api';
import { useNavigate, useParams } from 'react-router-dom';
import {
    ChevronLeft,
    Crosshair,
    GripVertical,
    ImagePlus,
    MapPinned,
    Minus,
    Plus,
    RefreshCcw,
    Save,
    SquareDashedMousePointer,
    X
} from 'lucide-react';
import { clubsService } from '../../../services';
import VenueSubnav from './VenueSubnav';
import StandClayConfigFields from './StandClayConfigFields';
import {
    buildPresentationDefaults,
    buildStandClayConfig,
    buildStandType,
    compareStandsByIdentity,
    disciplineTypes,
    extractPresentationDefaults,
    extractTargetLabels,
    formatStandIdentity,
    getDefaultTargetLabelsForDiscipline,
    getStandSetupStatus,
    parseStandIdentity
} from './standClayConfig';
import './ManageClubs.css';
import './CourseBuilder.css';

const DEFAULT_DISCIPLINE = 'Sporting';
const PDF_EXTENSION_PATTERN = /\.pdf($|\?)/i;
const GOOGLE_MAPS_LIBRARIES = ['places'];
const GOOGLE_MAPS_API_KEY = 'AIzaSyDu74EMNNqnvTVsAoz-375ia0BLH0A3HkU';
const DEFAULT_LIVE_MAP_ZOOM = 18;
const LIVE_MAP_CONTAINER_STYLE = {
    width: '100%',
    height: 'min(72vh, 760px)',
    borderRadius: '22px'
};

const isVisualMap = (mapUrl) => mapUrl && !PDF_EXTENSION_PATTERN.test(mapUrl);

const clamp = (value, min, max) => Math.min(max, Math.max(min, value));
const hasVenueCoordinates = (latitude, longitude) => Number.isFinite(latitude) && Number.isFinite(longitude) && !(latitude === 0 && longitude === 0);
const hasStandCoordinates = (stand) => Number.isFinite(Number(stand?.latitude)) && Number.isFinite(Number(stand?.longitude)) && !(Number(stand?.latitude) === 0 && Number(stand?.longitude) === 0);
const hasStandPlacement = (stand, mapSource) => (
    mapSource === 'live'
        ? hasStandCoordinates(stand)
        : stand?.courseMapX != null && stand?.courseMapY != null
);

const normaliseStand = (stand, fallbackOrder = 1) => {
    const identity = parseStandIdentity(stand?.name);
    const discipline = stand?.discipline || DEFAULT_DISCIPLINE;
    const resolvedLabels = extractTargetLabels(stand?.clayConfigJson);
    const targetLabels = resolvedLabels.length > 0
        ? resolvedLabels
        : getDefaultTargetLabelsForDiscipline(discipline);

    return {
        standId: stand?.standId ?? null,
        tempId: stand?.tempId || (stand?.standId ? `existing-${stand.standId}` : `new-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`),
        name: stand?.name || '',
        standNumber: identity.standNumber || '',
        standTitle: identity.standTitle || '',
        description: stand?.description || '',
        discipline,
        variant: stand?.variant || disciplineTypes[discipline]?.[0] || '',
        standType: stand?.standType || buildStandType(discipline, stand?.variant || disciplineTypes[discipline]?.[0] || ''),
        latitude: Number(stand?.latitude || 0),
        longitude: Number(stand?.longitude || 0),
        courseMapX: stand?.courseMapX ?? null,
        courseMapY: stand?.courseMapY ?? null,
        displayOrder: Number(stand?.displayOrder || fallbackOrder),
        targetLabels,
        presentationDefaults: buildPresentationDefaults(targetLabels, extractPresentationDefaults(stand?.clayConfigJson)),
        photos: Array.isArray(stand?.photos) ? stand.photos : [],
        videos: Array.isArray(stand?.videos) ? stand.videos : []
    };
};

const CourseBuilder = () => {
    const { clubId } = useParams();
    const navigate = useNavigate();
    const fileInputRef = useRef(null);
    const stageRef = useRef(null);
    const liveMapRef = useRef(null);

    const [clubName, setClubName] = useState('');
    const [clubLatitude, setClubLatitude] = useState(0);
    const [clubLongitude, setClubLongitude] = useState(0);
    const [maps, setMaps] = useState([]);
    const [selectedMapId, setSelectedMapId] = useState(null);
    const [mapSource, setMapSource] = useState('image');
    const [stands, setStands] = useState([]);
    const [selectedStandId, setSelectedStandId] = useState(null);
    const [linkExistingStandId, setLinkExistingStandId] = useState('');
    const [filterDiscipline, setFilterDiscipline] = useState('all');
    const [zoom, setZoom] = useState(1);
    const [customTargetInput, setCustomTargetInput] = useState('');
    const [isLoading, setIsLoading] = useState(true);
    const [isSaving, setIsSaving] = useState(false);
    const [isUploadingMap, setIsUploadingMap] = useState(false);
    const [error, setError] = useState(null);
    const [dragStandId, setDragStandId] = useState(null);
    const [draggingPinId, setDraggingPinId] = useState(null);
    const [liveMapZoom, setLiveMapZoom] = useState(DEFAULT_LIVE_MAP_ZOOM);

    const imageMaps = useMemo(
        () => (Array.isArray(maps) ? maps.filter((map) => isVisualMap(map.mapUrl)) : []),
        [maps]
    );

    const canUseLiveMap = useMemo(
        () => hasVenueCoordinates(Number(clubLatitude), Number(clubLongitude)),
        [clubLatitude, clubLongitude]
    );

    const liveMapCenter = useMemo(
        () => ({
            lat: Number(clubLatitude) || 54.5,
            lng: Number(clubLongitude) || -4
        }),
        [clubLatitude, clubLongitude]
    );

    const { isLoaded: isGoogleMapReady, loadError: googleMapLoadError } = useLoadScript({
        googleMapsApiKey: GOOGLE_MAPS_API_KEY,
        libraries: GOOGLE_MAPS_LIBRARIES
    });

    const selectedMap = useMemo(
        () => imageMaps.find((map) => String(map.clubMapId) === String(selectedMapId)) || null,
        [imageMaps, selectedMapId]
    );

    const sortedStands = useMemo(
        () => [...stands]
            .sort((left, right) => {
                const leftOrder = Number(left.displayOrder || 0);
                const rightOrder = Number(right.displayOrder || 0);

                if (leftOrder !== rightOrder) {
                    return leftOrder - rightOrder;
                }

                return compareStandsByIdentity(left, right);
            })
            .map((stand, index) => ({ ...stand, displayOrder: index + 1 })),
        [stands]
    );

    const filteredStands = useMemo(
        () => sortedStands.filter((stand) => filterDiscipline === 'all' || stand.discipline === filterDiscipline),
        [filterDiscipline, sortedStands]
    );

    const displayedStands = useMemo(
        () => filteredStands.filter((stand) => hasStandPlacement(stand, mapSource)),
        [filteredStands, mapSource]
    );

    const selectedStand = useMemo(
        () => stands.find((stand) => stand.tempId === selectedStandId) || null,
        [selectedStandId, stands]
    );

    const unplacedExistingStands = useMemo(
        () => sortedStands.filter((stand) => {
            if (!stand.standId) {
                return false;
            }

            if (mapSource === 'live') {
                return !hasStandCoordinates(stand);
            }

            return stand.courseMapX == null || stand.courseMapY == null;
        }),
        [mapSource, sortedStands]
    );

    useEffect(() => {
        const loadLayout = async () => {
            setIsLoading(true);
            setError(null);

            try {
                const layout = await clubsService.getCourseLayout(clubId);
                const nextStands = (Array.isArray(layout?.stands) ? layout.stands : []).map((stand, index) => normaliseStand(stand, index + 1));
                const nextClubLatitude = Number(layout?.clubLatitude || 0);
                const nextClubLongitude = Number(layout?.clubLongitude || 0);
                const nextSelectedMapId = layout?.selectedMapId || null;
                const nextMaps = Array.isArray(layout?.maps) ? layout.maps : [];
                const shouldUseLiveMap = !nextSelectedMapId && hasVenueCoordinates(nextClubLatitude, nextClubLongitude);

                setClubName(layout?.clubName || 'Venue');
                setClubLatitude(nextClubLatitude);
                setClubLongitude(nextClubLongitude);
                setMaps(nextMaps);
                setSelectedMapId(nextSelectedMapId);
                setMapSource(shouldUseLiveMap ? 'live' : 'image');
                setStands(nextStands);
                setSelectedStandId(nextStands[0]?.tempId || null);
                if (hasVenueCoordinates(nextClubLatitude, nextClubLongitude)) {
                    setLiveMapZoom(DEFAULT_LIVE_MAP_ZOOM);
                }
            } catch (fetchError) {
                console.error(fetchError);
                setError('Could not load the course builder. Please try again.');
            } finally {
                setIsLoading(false);
            }
        };

        loadLayout();
    }, [clubId]);

    useEffect(() => {
        if (!draggingPinId) {
            return undefined;
        }

        const handlePointerMove = (event) => {
            const stage = stageRef.current;
            if (!stage) {
                return;
            }

            const rect = stage.getBoundingClientRect();
            const nextX = clamp((event.clientX - rect.left) / rect.width, 0, 1);
            const nextY = clamp((event.clientY - rect.top) / rect.height, 0, 1);

            setStands((current) => current.map((stand) => (
                stand.tempId === draggingPinId
                    ? { ...stand, courseMapX: nextX, courseMapY: nextY }
                    : stand
            )));
        };

        const handlePointerUp = () => {
            setDraggingPinId(null);
        };

        window.addEventListener('pointermove', handlePointerMove);
        window.addEventListener('pointerup', handlePointerUp);

        return () => {
            window.removeEventListener('pointermove', handlePointerMove);
            window.removeEventListener('pointerup', handlePointerUp);
        };
    }, [draggingPinId]);

    useEffect(() => {
        setCustomTargetInput('');
    }, [selectedStandId]);

    const commitStandOrder = (nextStands) => {
        setStands(nextStands.map((stand, index) => ({
            ...stand,
            displayOrder: index + 1
        })));
    };

    const updateStand = (standId, updater) => {
        setStands((current) => current.map((stand) => (
            stand.tempId === standId
                ? { ...stand, ...updater(stand) }
                : stand
        )));
    };

    const updateSelectedStandField = (field, value) => {
        if (!selectedStand) {
            return;
        }

        updateStand(selectedStand.tempId, (stand) => {
            const nextStand = {
                ...stand,
                [field]: value
            };

            if (field === 'discipline') {
                const nextVariant = disciplineTypes[value]?.[0] || '';
                const nextLabels = getDefaultTargetLabelsForDiscipline(value);

                nextStand.variant = nextVariant;
                nextStand.targetLabels = nextLabels;
                nextStand.presentationDefaults = buildPresentationDefaults(nextLabels);
                nextStand.standType = buildStandType(value, nextVariant);
            }

            if (field === 'variant') {
                nextStand.standType = buildStandType(stand.discipline, value);
            }

            return nextStand;
        });
    };

    const placeStandAtPoint = (courseMapX, courseMapY, livePosition = null) => {
        if (!linkExistingStandId) {
            setError('Choose an existing stand first, then click the map to place it.');
            return;
        }

        const standToPlace = stands.find((stand) => stand.tempId === linkExistingStandId);
        if (!standToPlace) {
            setError('That stand could not be found. Refresh the page and try again.');
            return;
        }

        updateStand(standToPlace.tempId, () => ({
            courseMapX,
            courseMapY,
            latitude: livePosition?.lat ?? standToPlace.latitude,
            longitude: livePosition?.lng ?? standToPlace.longitude
        }));
        setSelectedStandId(standToPlace.tempId);
        setLinkExistingStandId('');
        setError(null);
    };

    const handleStageClick = (event) => {
        if (!selectedMap) {
            return;
        }

        const stage = stageRef.current;
        if (!stage || draggingPinId) {
            return;
        }

        const rect = stage.getBoundingClientRect();
        const courseMapX = clamp((event.clientX - rect.left) / rect.width, 0, 1);
        const courseMapY = clamp((event.clientY - rect.top) / rect.height, 0, 1);
        placeStandAtPoint(courseMapX, courseMapY);
    };

    const handleLiveMapClick = (event) => {
        if (mapSource !== 'live') {
            return;
        }

        const latLng = event.latLng;
        if (!latLng) {
            return;
        }

        placeStandAtPoint(null, null, {
            lat: latLng.lat(),
            lng: latLng.lng()
        });
    };

    const handleMapUpload = async (event) => {
        const file = event.target.files?.[0];
        if (!file) {
            return;
        }

        setIsUploadingMap(true);
        setError(null);

        try {
            const formData = new FormData();
            formData.append('file', file);
            formData.append('description', `Course map uploaded ${new Date().toLocaleDateString('en-GB')}`);
            await clubsService.uploadClubMap(clubId, formData);

            const layout = await clubsService.getCourseLayout(clubId);
            const nextMaps = Array.isArray(layout?.maps) ? layout.maps : [];
            setMaps(nextMaps);
            const visualMaps = nextMaps.filter((map) => isVisualMap(map.mapUrl));
            if (visualMaps[0]) {
                setSelectedMapId(visualMaps[0].clubMapId);
                setMapSource('image');
            }
        } catch (uploadError) {
            console.error(uploadError);
            setError('The map could not be uploaded. Please use a JPG, PNG, GIF, or WebP file under 10MB.');
        } finally {
            setIsUploadingMap(false);
            if (fileInputRef.current) {
                fileInputRef.current.value = '';
            }
        }
    };

    const handleSave = async () => {
        const validationErrors = [];
        const payloadStands = sortedStands.map((stand, index) => {
            const formattedName = formatStandIdentity(stand.standNumber, stand.standTitle, stand.name);
            if (!formattedName) {
                validationErrors.push('Every stand on the course needs a stand name or title before saving.');
            }

            if (!stand.discipline || !stand.variant) {
                validationErrors.push(`Stand '${formattedName || `#${index + 1}`}' needs a discipline and variant.`);
            }

            if (!Array.isArray(stand.targetLabels) || stand.targetLabels.length === 0) {
                validationErrors.push(`Stand '${formattedName || `#${index + 1}`}' needs at least one clay label.`);
            }

            if (!stand.standId) {
                validationErrors.push('Create stands on the stands page before placing them on the course map.');
            }

            return {
                standId: stand.standId,
                name: formattedName,
                description: stand.description,
                latitude: Number(stand.latitude || 0),
                longitude: Number(stand.longitude || 0),
                courseMapX: stand.courseMapX,
                courseMapY: stand.courseMapY,
                displayOrder: index + 1,
                discipline: stand.discipline,
                variant: stand.variant,
                standType: buildStandType(stand.discipline, stand.variant),
                clayConfigJson: buildStandClayConfig({
                    standName: formattedName,
                    discipline: stand.discipline,
                    variant: stand.variant,
                    targetLabels: stand.targetLabels,
                    presentationDefaults: stand.presentationDefaults
                })
            };
        });

        if (validationErrors.length > 0) {
            setError(validationErrors[0]);
            return;
        }

        setIsSaving(true);
        setError(null);

        try {
            const savedLayout = await clubsService.saveCourseLayout(clubId, {
                selectedMapId: mapSource === 'image' && selectedMapId ? Number(selectedMapId) : null,
                stands: payloadStands
            });

            const nextMaps = Array.isArray(savedLayout?.maps) ? savedLayout.maps : maps;
            const nextStands = (Array.isArray(savedLayout?.stands) ? savedLayout.stands : []).map((stand, index) => normaliseStand(stand, index + 1));
            setMaps(nextMaps);
            setSelectedMapId(savedLayout?.selectedMapId || null);
            setStands(nextStands);
            setSelectedStandId(nextStands.find((stand) => stand.standId === selectedStand?.standId)?.tempId || nextStands[0]?.tempId || null);
            if (window.toast) {
                window.toast.success('Course builder saved.');
            }
        } catch (saveError) {
            console.error(saveError);
            setError(saveError?.message || 'Could not save the course builder.');
        } finally {
            setIsSaving(false);
        }
    };

    const clearPin = () => {
        if (!selectedStand) {
            return;
        }

        updateStand(selectedStand.tempId, () => ({
            courseMapX: null,
            courseMapY: null,
            latitude: 0,
            longitude: 0
        }));
        setSelectedStandId(null);
    };

    const handleDropReorder = (targetStandId) => {
        if (!dragStandId || dragStandId === targetStandId) {
            return;
        }

        const current = [...sortedStands];
        const fromIndex = current.findIndex((stand) => stand.tempId === dragStandId);
        const toIndex = current.findIndex((stand) => stand.tempId === targetStandId);

        if (fromIndex < 0 || toIndex < 0) {
            return;
        }

        const [movedStand] = current.splice(fromIndex, 1);
        current.splice(toIndex, 0, movedStand);
        commitStandOrder(current);
        setDragStandId(null);
    };

    if (isLoading) {
        return (
            <div className="manage-clubs-page">
                <div className="manage-clubs-loading">
                    <div className="manage-clubs-spinner"></div>
                    <p>Loading course builder...</p>
                </div>
            </div>
        );
    }

    return (
        <div className="manage-clubs-page">
            <button
                className="club-dashboard-back"
                onClick={() => navigate(`/dashboard/club/${clubId}/stands`)}
            >
                <ChevronLeft size={18} />
                Back to {clubName} stands
            </button>

            <VenueSubnav clubId={clubId} />

            <div className="manage-section-header">
                <div className="manage-section-header-content">
                    <div className="manage-section-header-icon icon-green">
                        <MapPinned size={24} />
                    </div>
                    <div>
                        <h1>Course Builder</h1>
                        <p>Upload or choose a course map, then place your existing stands so the running order matches how shooters move around the ground.</p>
                    </div>
                </div>
                <button
                    className="manage-section-add-btn"
                    onClick={handleSave}
                    disabled={isSaving}
                >
                    <Save size={18} />
                    {isSaving ? 'Saving...' : 'Save Builder'}
                </button>
            </div>

            {error ? <div className="manage-clubs-error" role="alert">{error}</div> : null}

            <div className="course-builder-shell">
                <section className="course-builder-canvas-panel">
                    <div className="course-builder-toolbar">
                        <div className="course-builder-toolbar-group">
                            <label>
                                Map source
                                <select
                                    value={mapSource}
                                    onChange={(event) => setMapSource(event.target.value)}
                                >
                                    {canUseLiveMap ? <option value="live">Live venue map</option> : null}
                                    <option value="image">Uploaded course map</option>
                                </select>
                            </label>
                            <label>
                                Course map
                                <select
                                    value={selectedMapId || ''}
                                    onChange={(event) => setSelectedMapId(event.target.value ? Number(event.target.value) : null)}
                                    disabled={mapSource !== 'image'}
                                >
                                    <option value="">Choose a map</option>
                                    {imageMaps.map((map) => (
                                        <option key={map.clubMapId} value={map.clubMapId}>
                                            {map.description || `Map ${map.clubMapId}`}
                                        </option>
                                    ))}
                                </select>
                            </label>
                            <button
                                type="button"
                                className="dashboard-card-outline-btn"
                                onClick={() => fileInputRef.current?.click()}
                                disabled={isUploadingMap}
                            >
                                <ImagePlus size={16} />
                                {isUploadingMap ? 'Uploading...' : 'Upload map'}
                            </button>
                            <input
                                ref={fileInputRef}
                                type="file"
                                accept="image/*"
                                onChange={handleMapUpload}
                                hidden
                            />
                        </div>

                        <div className="course-builder-toolbar-group">
                            <label>
                                Stand to place
                                <select
                                    value={linkExistingStandId}
                                    onChange={(event) => setLinkExistingStandId(event.target.value)}
                                >
                                    <option value="">Choose an existing stand</option>
                                    {unplacedExistingStands.map((stand) => (
                                        <option key={stand.tempId} value={stand.tempId}>
                                            {formatStandIdentity(stand.standNumber, stand.standTitle, stand.name) || 'Untitled stand'}
                                        </option>
                                    ))}
                                </select>
                            </label>
                            {mapSource === 'image' ? (
                                <div className="course-builder-zoom">
                                    <button type="button" onClick={() => setZoom((current) => clamp(Number((current - 0.1).toFixed(2)), 0.6, 2.2))}>
                                        <Minus size={16} />
                                    </button>
                                    <span>{Math.round(zoom * 100)}%</span>
                                    <button type="button" onClick={() => setZoom((current) => clamp(Number((current + 0.1).toFixed(2)), 0.6, 2.2))}>
                                        <Plus size={16} />
                                    </button>
                                </div>
                            ) : null}
                        </div>
                    </div>

                    <div className="course-builder-stage-shell">
                        {mapSource === 'live' && canUseLiveMap ? (
                            <div className="course-builder-live-map-shell">
                                {googleMapLoadError ? (
                                    <div className="course-builder-empty-stage">
                                        <SquareDashedMousePointer size={40} />
                                        <h3>Google map could not load</h3>
                                        <p>Check the Google Maps API setup for this app, or switch to an uploaded course map.</p>
                                    </div>
                                ) : isGoogleMapReady ? (
                                    <GoogleMap
                                        mapContainerStyle={LIVE_MAP_CONTAINER_STYLE}
                                        center={liveMapCenter}
                                        zoom={liveMapZoom}
                                        onLoad={(map) => {
                                            liveMapRef.current = map;
                                        }}
                                        onZoomChanged={() => {
                                            const map = liveMapRef.current;
                                            if (map) {
                                                setLiveMapZoom(map.getZoom() || DEFAULT_LIVE_MAP_ZOOM);
                                            }
                                        }}
                                        onClick={handleLiveMapClick}
                                        options={{
                                            mapTypeControl: true,
                                            streetViewControl: false,
                                            fullscreenControl: true,
                                            clickableIcons: false
                                        }}
                                    >
                                        {displayedStands
                                            .filter((stand) => hasStandCoordinates(stand))
                                            .map((stand) => (
                                                <MarkerF
                                                    key={stand.tempId}
                                                    position={{ lat: Number(stand.latitude), lng: Number(stand.longitude) }}
                                                    draggable
                                                    onClick={() => setSelectedStandId(stand.tempId)}
                                                    onDragStart={() => setSelectedStandId(stand.tempId)}
                                                    onDragEnd={(event) => {
                                                        const latLng = event.latLng;
                                                        if (!latLng) {
                                                            return;
                                                        }

                                                        updateStand(stand.tempId, () => ({
                                                            latitude: latLng.lat(),
                                                            longitude: latLng.lng()
                                                        }));
                                                    }}
                                                    label={{
                                                        text: `${stand.displayOrder}`,
                                                        className: `course-builder-google-marker-label ${selectedStandId === stand.tempId ? 'selected' : ''}`
                                                    }}
                                                    title={stand.standTitle || stand.name || 'Stand'}
                                                />
                                            ))}
                                    </GoogleMap>
                                ) : (
                                    <div className="course-builder-empty-stage">
                                        <SquareDashedMousePointer size={40} />
                                        <h3>Loading live venue map</h3>
                                        <p>We’re centring the builder on your venue so pins can be dropped onto the real ground layout.</p>
                                    </div>
                                )}
                            </div>
                        ) : selectedMap ? (
                            <div className="course-builder-stage-viewport">
                                <div
                                    ref={stageRef}
                                    className="course-builder-stage"
                                    style={{ width: `${zoom * 100}%` }}
                                    onClick={handleStageClick}
                                >
                                    <img src={selectedMap.mapUrl} alt={selectedMap.description || `${clubName} course map`} />
                                    {displayedStands
                                        .filter((stand) => stand.courseMapX != null && stand.courseMapY != null)
                                        .map((stand) => (
                                            <button
                                                key={stand.tempId}
                                                type="button"
                                                className={`course-pin ${selectedStandId === stand.tempId ? 'selected' : ''}`}
                                                style={{
                                                    left: `${stand.courseMapX * 100}%`,
                                                    top: `${stand.courseMapY * 100}%`
                                                }}
                                                onClick={(event) => {
                                                    event.stopPropagation();
                                                    setSelectedStandId(stand.tempId);
                                                }}
                                                onPointerDown={(event) => {
                                                    event.stopPropagation();
                                                    setSelectedStandId(stand.tempId);
                                                    setDraggingPinId(stand.tempId);
                                                }}
                                            >
                                                <span className="course-pin-order">{stand.displayOrder}</span>
                                                <span className="course-pin-label">
                                                    {stand.standTitle || stand.name || 'New stand'}
                                                </span>
                                            </button>
                                        ))}
                                </div>
                            </div>
                        ) : (
                            <div className="course-builder-empty-stage">
                                <SquareDashedMousePointer size={40} />
                                <h3>{canUseLiveMap ? 'Choose how you want to build' : 'Choose a map to start building'}</h3>
                                <p>
                                    {canUseLiveMap
                                        ? 'Use the live venue map to drop pins onto the real ground, or switch to an uploaded image if you already have a printed course plan.'
                                        : 'Use an uploaded course image rather than a PDF if you want to place and drag stand pins visually.'}
                                </p>
                            </div>
                        )}
                    </div>

                    <div className="course-builder-helper-row">
                        <div className="course-builder-helper-card">
                            <Crosshair size={16} />
                            <span>{mapSource === 'live' ? 'Choose a stand, then click the live map to place it where shooters will stand.' : 'Choose a stand, then click the map to place its pin.'}</span>
                        </div>
                        <div className="course-builder-helper-card">
                            <RefreshCcw size={16} />
                            <span>{mapSource === 'live' ? 'Drag markers to fine tune their ground position, then save the running route.' : 'Drag a pin to fine tune its position after it is placed.'}</span>
                        </div>
                    </div>
                </section>

                <aside className="course-builder-sidebar">
                    <div className="course-builder-sidebar-card">
                        <div className="course-builder-sidebar-header">
                            <div>
                                <h2>Stand Route</h2>
                                <p>{displayedStands.length} plotted stand{displayedStands.length === 1 ? '' : 's'} in running order</p>
                            </div>
                            <label>
                                Filter
                                <select
                                    value={filterDiscipline}
                                    onChange={(event) => setFilterDiscipline(event.target.value)}
                                >
                                    <option value="all">All disciplines</option>
                                    {Object.keys(disciplineTypes).map((discipline) => (
                                        <option key={discipline} value={discipline}>{discipline}</option>
                                    ))}
                                </select>
                            </label>
                        </div>

                        <div className="course-builder-stand-list">
                            {displayedStands.length === 0 ? (
                                <div className="course-builder-empty-editor">
                                    <MapPinned size={34} />
                                    <h3>No stands plotted yet</h3>
                                    <p>Choose an existing stand above, then click the map to add it to the course route.</p>
                                </div>
                            ) : displayedStands.map((stand) => {
                                const displayName = formatStandIdentity(stand.standNumber, stand.standTitle, stand.name) || 'Untitled stand';
                                const status = getStandSetupStatus(stand);
                                const isSelected = selectedStandId === stand.tempId;

                                return (
                                    <button
                                        key={stand.tempId}
                                        type="button"
                                        className={`course-builder-stand-row ${isSelected ? 'selected' : ''}`}
                                        draggable
                                        onClick={() => setSelectedStandId(stand.tempId)}
                                        onDragStart={() => setDragStandId(stand.tempId)}
                                        onDragOver={(event) => event.preventDefault()}
                                        onDrop={() => handleDropReorder(stand.tempId)}
                                    >
                                        <span className="course-builder-stand-order">{stand.displayOrder}</span>
                                        <div className="course-builder-stand-copy">
                                            <strong>{displayName}</strong>
                                            <span>{stand.variant || stand.discipline || 'Choose setup'}</span>
                                        </div>
                                        <span className={`course-builder-status ${status}`}>{status === 'configured' ? 'Configured' : 'Needs clay setup'}</span>
                                        <GripVertical size={16} />
                                    </button>
                                );
                            })}
                        </div>
                    </div>

                    <div className="course-builder-sidebar-card">
                        {selectedStand ? (
                            <>
                                <div className="course-builder-sidebar-header">
                                    <div>
                                        <h2>Edit Stand</h2>
                                        <p>Use the map first, then fill in only the bits staff need.</p>
                                    </div>
                                    <button type="button" className="course-builder-clear-btn" onClick={() => setSelectedStandId(null)}>
                                        <X size={16} />
                                    </button>
                                </div>

                                <div className="dashboard-form-grid">
                                    <div className="form-group">
                                        <label>Stand Number</label>
                                        <input
                                            type="number"
                                            min="1"
                                            value={selectedStand.standNumber}
                                            onChange={(event) => updateSelectedStandField('standNumber', event.target.value)}
                                            placeholder="4"
                                        />
                                    </div>

                                    <div className="form-group">
                                        <label>Stand Name / Title</label>
                                        <input
                                            type="text"
                                            value={selectedStand.standTitle}
                                            onChange={(event) => updateSelectedStandField('standTitle', event.target.value)}
                                            placeholder="Rabbits, English Skeet, 90 Tower"
                                        />
                                    </div>

                                    <div className="form-group col-span-2">
                                        <label>Presentation Summary / Tips</label>
                                        <textarea
                                            value={selectedStand.description}
                                            onChange={(event) => updateSelectedStandField('description', event.target.value)}
                                            placeholder="Quick note to help staff and shooters recognise this layout"
                                        />
                                    </div>

                                    <div className="form-group">
                                        <label>Discipline</label>
                                        <select
                                            value={selectedStand.discipline}
                                            onChange={(event) => updateSelectedStandField('discipline', event.target.value)}
                                        >
                                            {Object.keys(disciplineTypes).map((discipline) => (
                                                <option key={discipline} value={discipline}>{discipline}</option>
                                            ))}
                                        </select>
                                    </div>

                                    <div className="form-group">
                                        <label>Variant</label>
                                        <select
                                            value={selectedStand.variant}
                                            onChange={(event) => updateSelectedStandField('variant', event.target.value)}
                                        >
                                            {(disciplineTypes[selectedStand.discipline] || []).map((variant) => (
                                                <option key={variant} value={variant}>{variant}</option>
                                            ))}
                                        </select>
                                    </div>

                                    <StandClayConfigFields
                                        discipline={selectedStand.discipline}
                                        targetLabels={selectedStand.targetLabels}
                                        presentationDefaults={selectedStand.presentationDefaults}
                                        customTargetInput={customTargetInput}
                                        onCustomTargetInputChange={setCustomTargetInput}
                                        onTargetLabelsChange={(nextLabels) => updateStand(selectedStand.tempId, (stand) => ({
                                            targetLabels: nextLabels,
                                            presentationDefaults: buildPresentationDefaults(nextLabels, stand.presentationDefaults)
                                        }))}
                                        onPresentationDefaultsChange={(nextDefaults) => updateStand(selectedStand.tempId, () => ({
                                            presentationDefaults: nextDefaults
                                        }))}
                                    />
                                </div>

                                <div className="course-builder-location-card">
                                    <span className="course-builder-location-label">{mapSource === 'live' ? 'Ground position' : 'Map position'}</span>
                                    <strong>
                                        {mapSource === 'live'
                                            ? (hasStandCoordinates(selectedStand)
                                                ? `${Number(selectedStand.latitude).toFixed(6)}, ${Number(selectedStand.longitude).toFixed(6)}`
                                                : 'Not placed on the live map yet')
                                            : (selectedStand.courseMapX != null && selectedStand.courseMapY != null
                                                ? `${(selectedStand.courseMapX * 100).toFixed(1)}%, ${(selectedStand.courseMapY * 100).toFixed(1)}%`
                                                : 'Not placed on the map yet')}
                                    </strong>
                                </div>

                                <div className="course-builder-editor-actions">
                                    <button type="button" className="dashboard-card-outline-btn" onClick={clearPin}>
                                        Clear pin
                                    </button>
                                </div>
                            </>
                        ) : (
                            <div className="course-builder-empty-editor">
                                <MapPinned size={34} />
                                <h3>Select a stand pin</h3>
                                <p>Pick an existing stand from the route list, or create a new one on the stands page before placing it here.</p>
                            </div>
                        )}
                    </div>
                </aside>
            </div>
        </div>
    );
};

export default CourseBuilder;
